// Dance-count loaders: each pattern is the count a dancer would say out loud.
// `beats` is what's on screen; `steps` is which beat is lit, and for how long.

// Every loader runs this much faster than the base durations below (#1885).
export const LOADER_TEMPO = 1.3;

// Once a loader appears it stays up at least this long (#1885).
export const MIN_LOADER_MS = 1500;

// With prefers-reduced-motion the counts step this much slower, by colour only (#1940).
export const REDUCED_MOTION_SLOWDOWN = 2.5;

export interface Beat {
  label: string;
  // [fontSize rem, fontWeight] when lit and when resting
  on: [number, number];
  off: [number, number];
  italic?: boolean;
  // widen the letter spacing when lit (held, heavy beats)
  track?: boolean;
}

export interface BeatPattern {
  beats: Beat[];
  // [index into beats (null = rest), base duration ms]
  steps: [number | null, number][];
  // the signature beat; with reduced motion it stays printed big instead of growing
  accent: number;
}

const QUICK: Pick<Beat, 'on' | 'off'> = { on: [0.9, 600], off: [0.65, 300] };
const HEAVY: Pick<Beat, 'on' | 'off'> = { on: [1.4, 800], off: [0.9, 300] };
const COUNT: Pick<Beat, 'on' | 'off'> = { on: [1.15, 700], off: [0.85, 400] };
const ACCENT: Pick<Beat, 'on' | 'off'> = { on: [0.95, 500], off: [0.7, 400] };

export const BEAT_PATTERNS = {
  // 1-2-3-(4)-5-6-7-(8): quick-quick-slow, beats 4 & 8 rest
  salsa: {
    beats: ['1', '2', '3', '·', '5', '6', '7', '·'].map((label): Beat =>
      label === '·'
        ? { label, on: [1.15, 700], off: [0.7, 300] }
        : { label, ...COUNT }
    ),
    steps: [[0, 380], [1, 380], [2, 660], [null, 280], [4, 380], [5, 380], [6, 660], [null, 280]],
    accent: 2,
  },
  // 5-6-7-8 count-in
  countIn: {
    beats: ['5', '6', '7', '8'].map((label): Beat => ({ label, on: [1.25, 700], off: [0.85, 400] })),
    steps: [[0, 420], [1, 420], [2, 420], [3, 420]],
    accent: 0,
  },
  // boom - chick - chick: heavy downbeat, two light offbeats
  zouk: {
    beats: [
      { label: 'boom', ...HEAVY, track: true },
      { label: 'chick', ...QUICK },
      { label: 'chick', ...QUICK },
    ],
    steps: [[0, 680], [1, 340], [2, 340]],
    accent: 0,
  },
  // 1-2-3-tap: even beats, "tap" styled distinctly
  bachata: {
    beats: [
      { label: '1', ...COUNT },
      { label: '2', ...COUNT },
      { label: '3', ...COUNT },
      { label: 'tap', ...ACCENT, italic: true },
    ],
    steps: [[0, 420], [1, 420], [2, 420], [3, 420]],
    accent: 3,
  },
  // quick - quick - slow: two light quicks, heavy held slow
  kizomba: {
    beats: [
      { label: 'quick', ...QUICK },
      { label: 'quick', ...QUICK },
      { label: 'slow', ...HEAVY, track: true },
    ],
    steps: [[0, 340], [1, 340], [2, 820]],
    accent: 2,
  },
  // step - step - shuffle - step
  kizombaStep: {
    beats: [
      { label: 'step', on: [1.1, 700], off: [0.8, 400] },
      { label: 'step', on: [1.1, 700], off: [0.8, 400] },
      { label: 'shuffle', ...ACCENT, italic: true },
      { label: 'step', on: [1.1, 700], off: [0.8, 400] },
    ],
    steps: [[0, 440], [1, 440], [2, 300], [3, 520]],
    accent: 2,
  },
  // 1 - 2 - marca: two even steps, the mark held longest
  kizombaMarca: {
    beats: [
      { label: '1', ...COUNT },
      { label: '2', ...COUNT },
      { label: 'marca', on: [1.3, 700], off: [0.85, 300], italic: true, track: true },
    ],
    steps: [[0, 420], [1, 420], [2, 860]],
    accent: 2,
  },
} satisfies Record<string, BeatPattern>;

export const DANCE_LOADER_TYPES = [
  'equalizer',
  'salsa',
  'count-in',
  'zouk',
  'bachata',
  'kizomba',
  'kizomba-step',
  'kizomba-marca',
] as const;
export type DanceLoaderType = (typeof DANCE_LOADER_TYPES)[number];

// Pages with no matching style pick from the original five.
export const RANDOM_LOADER_POOL: DanceLoaderType[] = ['equalizer', 'salsa', 'count-in', 'zouk', 'bachata'];

// Matched against the lowercased style name. First family that matches wins.
const STYLE_FAMILIES: { keywords: string[]; loaders: DanceLoaderType[] }[] = [
  { keywords: ['kiz', 'semba', 'tarraxa', 'tarraxinha'], loaders: ['kizomba', 'kizomba-step', 'kizomba-marca'] },
  { keywords: ['zouk', 'lambada'], loaders: ['zouk'] },
  { keywords: ['bachata'], loaders: ['bachata'] },
  { keywords: ['salsa', 'mambo'], loaders: ['salsa'] },
];

export type DanceStyleLike = string | { name: string } | null | undefined;

// Loaders that fit these dance styles, in the order the styles are given.
// Empty when none of the styles has its own loader.
export function loaderPoolForStyles(styles: DanceStyleLike[] | undefined): DanceLoaderType[] {
  for (const style of styles ?? []) {
    const name = (typeof style === 'string' ? style : style?.name ?? '').toLowerCase();
    if (!name) continue;
    const family = STYLE_FAMILIES.find((f) => f.keywords.some((k) => name.includes(k)));
    if (family) return family.loaders;
  }
  return [];
}

// ── Tiny loaders (buttons, inputs, dropdowns) ───────────────────────────────
// A random one of these replaces every small Mantine spinner site-wide.
export const TINY_LOADER_TYPES = ['equalizer', 'dots', 'footsteps'] as const;
export type TinyLoaderType = (typeof TINY_LOADER_TYPES)[number];

export interface TinyRhythm {
  // number of dots on screen
  beats: number;
  // [lit dot (null = rest), base duration ms, accented?]
  steps: [number | null, number, boolean?][];
}

// The beat dots and footsteps step in the page's rhythm.
export const TINY_RHYTHMS = {
  zouk: { beats: 3, steps: [[0, 680, true], [1, 340], [2, 340]] },
  kizomba: { beats: 3, steps: [[0, 340], [1, 340], [2, 820, true]] },
  bachata: { beats: 4, steps: [[0, 420], [1, 420], [2, 420], [3, 420, true]] },
  salsa: { beats: 3, steps: [[0, 380], [1, 380], [2, 660, true], [null, 280]] },
  countIn: { beats: 4, steps: [[0, 420], [1, 420], [2, 420], [3, 420]] },
} satisfies Record<string, TinyRhythm>;

const RHYTHM_FOR_LOADER: Partial<Record<DanceLoaderType, keyof typeof TINY_RHYTHMS>> = {
  kizomba: 'kizomba',
  zouk: 'zouk',
  bachata: 'bachata',
  salsa: 'salsa',
};

// The page's rhythm for the tiny loaders; a plain 5-6-7-8 when no style matches.
export function tinyRhythmForStyles(styles: DanceStyleLike[] | undefined): TinyRhythm {
  const [first] = loaderPoolForStyles(styles);
  const key = first ? RHYTHM_FOR_LOADER[first] : undefined;
  return TINY_RHYTHMS[key ?? 'countIn'];
}
